import NItem, { type NAnimationOptions, type NCssObject, type NItemAttributes } from "@/lib/NItem.ts";

// type NValue = NItem|string|null|boolean|number;
export type NInput = string|N|NItem|NodeList|EventTarget|null;
type NList = Array<NItem>;

export default function n(input: NInput, parent: NInput|null = null)
{
    if (input instanceof N) {
        return input;
    }

    return new N(input, parent);
}

export class N
{
    _n = true as const;
    #items: NList|null = null;

    constructor(input: NInput, parent: NInput|null = null)
    {
        this.#items = this.#inputToItems(input, parent);
    }

    get length(): number
    {
        return this.#items?.length ?? 0;
    }

    get y(): number { return this.get()?.y as number; }
    get x(): number { return this.get()?.x as number; }
    get top(): number { return this.get()?.top as number; }
    get bottom(): number { return this.get()?.bottom as number; }
    get left(): number { return this.get()?.left as number; }
    get right(): number { return this.get()?.right as number; }
    get width(): number { return this.get()?.width as number; }
    get height(): number { return this.get()?.height as number; }

    find(selector: string): N
    {
        return n(selector, this);
    }

    show(
        opts: NAnimationOptions = {},
        cb: ((item: NItem) => void)|undefined = undefined
    ): N
    {
        this.#apply((item: NItem) => {
            item.show(opts).then(() => cb?.(item));
        });
        return this;
    }

    hide(
        opts: NAnimationOptions = {},
        cb: ((item: NItem) => void)|undefined = undefined
    )
    {
        this.#apply((item: NItem) => {
            item.hide(opts).then(() => cb?.(item));
        });
        return this;
    }

    remove()
    {
        this.#apply((item: NItem) => {
            item.remove();
        });
    }

    html<T extends Element>(content: string): N
    {
        this.#apply((item: NItem) => {
            item.html<T>(content);
        });
        return this;
    }

    append<T extends Element>(input: NInput): N
    {
        const inputItem = n(input)?.get?.();
        this.#apply((item: NItem) => {
            if (inputItem) {
                item.append<T>(inputItem);
            }
        });
        return this;
    }

    parent(selector: string|null = null): N|null
    {
        const item = this.get();

        if (item) {
            const parent = item.parent(selector);
            if (parent) {
                return n(parent);
            }
        }

        return null;
    }

    trigger(
        eventName: string,
        options: CustomEventInit = {}
    ): boolean | undefined
    {
        const e = new CustomEvent(eventName, {
            bubbles: true,
            cancelable: true,
            ...options,
        });
        return this.getNode<Element>()?.dispatchEvent(e);
    }

    on(
        eventName: string,
        cb: (e: Event) => void,
        opts: AddEventListenerOptions = {}
    ): N
    {
        this.#apply((item: NItem) => item.on(eventName, cb, opts));
        return this;
    }

    off(eventName: string): N
    {
        this.#apply((item: NItem) => item.off(eventName));
        return this;
    }

    once(
        eventName: string,
        cb: (e: Event) => void,
    ): N
    {
        return this.on(eventName, cb, { once: true });
    }

    css(
        keyOrValues: NCssObject|string,
        value: null|string|number|undefined = undefined
    ): N
    {
        this.#apply((item: NItem) => {
            return item.css(keyOrValues, value);
        });
        return this;
    }

    getAttributes(): NItemAttributes
    {
        return this.get()?.getAttributes() ?? {};
    }

    getAttribute(name: string): string|null
    {
        return this.get()?.getAttribute(name) ?? null;
    }

    setAttribute(name: string, value: string): N
    {
        this.#apply((item: NItem) => {
            return item.setAttribute(name, value);
        });
        return this;
    }

    removeAttribute(name: string): N
    {
        this.#apply((item: NItem) => {
            return item.removeAttribute(name);
        });
        return this;
    }

    hasClass<T extends Element>(className: string): boolean|null
    {
        const node = this.getNode?.<T>();
        return node && node.classList.contains(className);
    }

    addClass(className: string): N
    {
        this.#apply((item: NItem) => {
            if (item instanceof NItem) {
                item.addClass(className);
            }
        });
        return this;
    }

    removeClass(className: string): N
    {
        this.#apply((item: NItem) => {
            item.removeClass(className);
        });
        return this;
    }

    getNode<T>(index: number = 0): null|T
    {
        return (this.#items?.[index]) ? this.#items[index].getNode() as T : null;
    }

    get(index: number = 0): null|NItem
    {
        return (this.#items?.[index] as NItem) ?? null;
    }

    getItems(): NList|null
    {
        return this.#items;
    }

    each(cb: (item: NItem, k: number) => void): void
    {
        if (this.#items !== null) {
            for (let k = 0; k < this.#items.length; k++) {
                cb(this.#items[k], k);
            }
        }
    }

    #apply(cb: (item: NItem) => void): void
    {
        if (this.#items === null) return;
        for (let k = 0; k < this.#items.length; k++) {
            cb(this.#items[k]);
        }
    }

    #inputToItems(input: NInput, parent: NInput | null = null): null | NList
    {
        let items: NList | null = [];

        const looksLikeHtml =
            typeof input === "string" && input.trim().startsWith("<");

        if (input instanceof NodeList) {
            for (const node of input) {
                if (node instanceof Element) {
                    items.push(new NItem(node));
                }
            }
        } else if (input instanceof Element) {
            items = [new NItem(input)];
        } else if (typeof input === "string" && looksLikeHtml) {
            const doc = new DOMParser().parseFromString(input, "text/html");
            for (const child of doc.body.children) {
                items.push(new NItem(child));
            }
        } else if (typeof input === "string") {
            const parentNode = parent
                ? this.#inputToNode(parent) ?? document
                : document;
            const result = parentNode.querySelectorAll(input);
            items = this.#inputToItems(result);
        } else if (input instanceof N) {
            items = input.getItems();
        } else if (input instanceof NItem) {
            items = [input];
        }

        return (!items?.length) ? null : items;
    }

    #inputToNode(input: NInput, parent: NInput|null = null): null|Element
    {
        let item: Element|null|undefined = null;

        if (input instanceof NodeList) {
            item = (input?.[0] as Element) ?? null;
        } else if (input instanceof Element) {
            item = input;
        } else if (typeof input === 'string') {
            const parentNode = parent
                ? this.#inputToNode(parent) ?? document
                : document;
            item = parentNode.querySelectorAll(input)?.[0] ?? null;
        } else if (input instanceof N) {
            item = input.get()?.getNode();
        }

        return item ?? null;
    }
}
